import React, { useEffect, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Input, Select, Space, Table, Tag, Typography } from 'antd'
import { EditOutlined, LinkOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons'
import moment from 'moment'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'

import NoteModal from './NoteModal'

const CUSTOMER_VOICE_FIELDS = gql`
  fragment CustomerVoiceFields on AdminCustomerVoice {
    id
    name
    email
    phoneNumber
    ip
    description
    pageUrl
    note
    contactBack
    lineUid
    createdAt {
      raw
    }
    order {
      id
      number
    }
    user {
      id
      name
      email
      customerNo
    }
  }
`

const CUSTOMER_VOICES_QUERY = gql`
  query CustomerVoices($first: Int, $after: String, $query: String, $hasNote: Boolean) {
    customerVoices(first: $first, after: $after, query: $query, hasNote: $hasNote) {
      totalCount
      nodes {
        ...CustomerVoiceFields
      }
    }
  }
  ${CUSTOMER_VOICE_FIELDS}
`

const CUSTOMER_VOICE_QUERY = gql`
  query CustomerVoice($id: ID) {
    customerVoices(id: $id, first: 1) {
      nodes {
        ...CustomerVoiceFields
      }
    }
  }
  ${CUSTOMER_VOICE_FIELDS}
`

const PAGE_SIZE = 20
const NOTE_FILTERS = { all: undefined, with: true, without: false }

const offsetCursor = (page) => (page > 1 ? btoa(String((page - 1) * PAGE_SIZE)).replace(/=+$/, '') : null)

const formatDateTime = (createdAt) => (createdAt && createdAt.raw ? moment(createdAt.raw).format('DD/MM/YYYY HH:mm') : '-')

const renderSender = (t) => (voice) => (
  <Space direction='vertical' size={0}>
    <span>{voice.name || (voice.user && voice.user.name) || '-'}</span>
    {voice.phoneNumber && <span><PhoneOutlined /> {voice.phoneNumber}</span>}
    {voice.email && <span><MailOutlined /> {voice.email}</span>}
    {voice.lineUid && <Tag color='green'>LINE</Tag>}
    {voice.user && voice.user.customerNo && (
      <Link to={`/customers/${voice.user.customerNo}`}>{t('customerVoices:customer')} {voice.user.customerNo}</Link>
    )}
  </Space>
)

const getColumns = (t, onEditNote) => [
  {
    key: 'createdAt',
    title: t('customerVoices:createdAt'),
    dataIndex: 'createdAt',
    width: 140,
    render: formatDateTime
  },
  {
    key: 'sender',
    title: t('customerVoices:sender'),
    render: renderSender(t)
  },
  {
    key: 'contactBack',
    title: t('customerVoices:contactBack'),
    dataIndex: 'contactBack',
    render: (contactBack) => (contactBack ? t(`customerVoices:contactBackOptions.${contactBack}`) : '-')
  },
  {
    key: 'description',
    title: t('customerVoices:description'),
    dataIndex: 'description',
    render: (description) => <div style={{ whiteSpace: 'pre-wrap', minWidth: 200 }}>{description}</div>
  },
  {
    key: 'order',
    title: t('customerVoices:order'),
    dataIndex: ['order', 'number'],
    render: (number) => (number ? <Link to={`/inquiries/${number}`}>{number}</Link> : '-')
  },
  {
    key: 'pageUrl',
    title: t('customerVoices:pageUrl'),
    dataIndex: 'pageUrl',
    render: (pageUrl) => (pageUrl ? (
      <Typography.Link href={pageUrl} target='_blank' rel='noopener noreferrer' style={{ wordBreak: 'break-all' }}>
        <LinkOutlined /> {pageUrl}
      </Typography.Link>
    ) : '-')
  },
  {
    key: 'ip',
    title: t('customerVoices:ip'),
    dataIndex: 'ip'
  },
  {
    key: 'note',
    title: t('customerVoices:note'),
    dataIndex: 'note',
    render: (note) => <div style={{ whiteSpace: 'pre-wrap', minWidth: 160 }}>{note || '-'}</div>
  },
  {
    key: 'action',
    width: 100,
    render: (voice) => (
      <Button size='small' icon={<EditOutlined />} onClick={() => onEditNote(voice.id)}>
        {t('customerVoices:editNote')}
      </Button>
    )
  }
]

const CustomerVoicesIndexPage = () => {
  const { t } = useTranslation(['customerVoices', 'common'])
  const history = useHistory()
  const location = useLocation()
  const [page, setPage] = useState(1)
  const [query, setQuery] = useState('')
  const [noteFilter, setNoteFilter] = useState('all')

  const selectedId = new URLSearchParams(location.search).get('id')

  useEffect(() => { document.title = 'Customer Voices | OSH CRM' }, [])

  const { data, loading } = useQuery(CUSTOMER_VOICES_QUERY, {
    variables: {
      first: PAGE_SIZE,
      after: offsetCursor(page),
      query: query || undefined,
      hasNote: NOTE_FILTERS[noteFilter]
    },
    fetchPolicy: 'cache-and-network'
  })

  const selected = useQuery(CUSTOMER_VOICE_QUERY, {
    variables: { id: selectedId },
    skip: !selectedId
  })

  const setSelectedId = (id) => {
    const params = new URLSearchParams(location.search)
    if (id) {
      params.set('id', id)
    } else {
      params.delete('id')
    }
    const search = params.toString()
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' })
  }

  const connection = (data && data.customerVoices) || {}
  const selectedNodes = (selected.data && selected.data.customerVoices && selected.data.customerVoices.nodes) || []
  const selectedVoice = selectedNodes[0]

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[{ path: '/customer_voices', name: t('common:customerVoices') }]}>
        <ContentLayout>
          <Space wrap style={{ marginBottom: 16 }}>
            <Input.Search
              allowClear
              placeholder={t('customerVoices:searchPlaceholder')}
              style={{ width: 320 }}
              onSearch={(value) => { setQuery(value.trim()); setPage(1) }}
            />
            <Select
              value={noteFilter}
              style={{ width: 180 }}
              onChange={(value) => { setNoteFilter(value); setPage(1) }}
              options={[
                { value: 'all', label: t('customerVoices:noteFilter.all') },
                { value: 'without', label: t('customerVoices:noteFilter.without') },
                { value: 'with', label: t('customerVoices:noteFilter.with') }
              ]}
            />
          </Space>
          <Table
            rowKey='id'
            size='small'
            scroll={{ x: true }}
            loading={loading}
            columns={getColumns(t, setSelectedId)}
            dataSource={connection.nodes || []}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: connection.totalCount || 0,
              showSizeChanger: false,
              onChange: setPage
            }}
          />
          <NoteModal
            open={Boolean(selectedId)}
            loading={selected.loading}
            customerVoice={selectedVoice}
            onClose={() => setSelectedId(null)}
          />
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

export default CustomerVoicesIndexPage
